import { useEffect, useState, useCallback } from "react";
import api, { errorMessage } from "../lib/api";
import { PageHeader, ActionButton, GhostButton, FormField, inputClass, PillBadge } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { SingleSelectDropdown } from "../components/SingleSelectDropdown";
import { Plus, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const OP_ROLES = ["admin", "super_admin", "supervisor", "dispatcher", "operations_manager", "chief_security_officer"];
const DECIDE_ROLES = ["admin", "super_admin", "supervisor", "operations_manager", "chief_security_officer"];

const empty = { guard_id: "", start_date: "", end_date: "", reason: "" };

const isoDay = (d) => d.toISOString().slice(0, 10);
// Forward-looking window: today → today + N days (inclusive). Mirrors the
// pattern used on the Availability page so both surfaces feel consistent.
const forwardRange = (days) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const end = new Date(today); end.setDate(end.getDate() + days);
  return { start: isoDay(today), end: isoDay(end) };
};

export default function VacationRequests() {
  const { user } = useAuth();
  const isOp = OP_ROLES.includes(user?.role);
  const canDecide = DECIDE_ROLES.includes(user?.role);
  const [rows, setRows] = useState([]);
  const [guards, setGuards] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [decidingId, setDecidingId] = useState(null); // shows the approval-options modal
  // Forward-looking window (in days) used to filter which requests are shown.
  // Defaults to 2 weeks — same default as the Availability tab.
  const [days, setDays] = useState(14);
  const window_ = forwardRange(days);
  // A request is shown if its [start_date, end_date] intersects the window.
  const visibleRows = rows.filter((r) =>
    r.start_date && r.end_date && r.end_date >= window_.start && r.start_date <= window_.end
  );

  const load = useCallback(async () => {
    try {
      const [r, g] = await Promise.all([
        api.get("/vacation-requests"),
        isOp ? api.get("/guards") : Promise.resolve({ data: [] }),
      ]);
      setRows(r.data || []);
      setGuards((g.data || []).filter((x) => (x.status || "active") !== "inactive"));
    } catch (e) { toast.error(errorMessage(e, "Load failed")); }
    finally { setLoading(false); }
  }, [isOp]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (isOp && !form.guard_id) return toast.error("Pick a ");
    if (!form.start_date || !form.end_date) return toast.error("Start and end dates required");
    if (form.end_date < form.start_date) return toast.error("End must be on or after start");
    try {
      const payload = { ...form };
      if (!isOp) delete payload.guard_id;
      await api.post("/vacation-requests", payload);
      toast.success("Request submitted");
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(errorMessage(err, "Submit failed")); }
  };

  const decide = async (id, status, opts = {}) => {
    let note = null;
    if (status === "rejected") {
      note = window.prompt("Reason for rejection (shown to guard):", "");
      if (note === null) return;
    }
    try {
      const body = { status, admin_note: note };
      if (status === "approved") {
        body.add_availability = opts.add_availability ?? true;
        body.unassign_from_shifts = opts.unassign_from_shifts ?? true;
      }
      const r = await api.patch(`/vacation-requests/${id}`, body);
      if (status === "approved") {
        toast.success(`Approved · +${r.data?.availability_added || 0} availability, ${r.data?.shifts_unassigned || 0} shift(s) unassigned`);
      } else {
        toast.success(`Request ${status}`);
      }
      setDecidingId(null);
      load();
    } catch (e) { toast.error(errorMessage(e, "Decision failed")); }
  };

  const remove = async (id) => {
    if (!window.confirm("Cancel this request?")) return;
    try { await api.delete(`/vacation-requests/${id}`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(errorMessage(e, "Cancel failed")); }
  };

  const toneFor = (s) => (s === "approved" ? "green" : s === "rejected" ? "red" : "amber");

  return (
    <div>
      <PageHeader
        title="Vacation Requests"
        subtitle="Time-off requests. Approved dates highlight on the Roster in yellow."
        action={
          <ActionButton data-testid="add-vacation-button" onClick={() => setOpen(true)}>
            <Plus className="size-4" /> New Request
          </ActionButton>
        }
      />
      <div className="p-8 space-y-4">
        {/* Period filter — restricts which requests are shown based on how far forward the window looks. */}
        <div className="border border-[#27272A] bg-[#121212] p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="label-mute mr-1">Period</div>
            {[
              { d: 7, label: "1 Week" },
              { d: 14, label: "2 Weeks" },
              { d: 30, label: "1 Month" },
            ].map(({ d, label }) => {
              const active = days === d;
              return (
                <button
                  key={d}
                  type="button"
                  data-testid={`vac-period-${d}d`}
                  onClick={() => setDays(d)}
                  className={`text-[10px] uppercase tracking-widest border px-2.5 py-1 transition-colors ${active ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/5" : "border-[#27272A] text-zinc-400 hover:border-[#FF3B30] hover:text-[#FF3B30]"}`}
                >{label}</button>
              );
            })}
            <div className="mx-2 h-6 w-px bg-[#27272A]" />
            <div className="text-[10px] font-mono text-zinc-500" data-testid="vac-period-window">
              {window_.start} → {window_.end}
            </div>
            <div className="ml-auto text-[10px] font-mono text-zinc-500" data-testid="vac-period-count">
              {visibleRows.length} of {rows.length}
            </div>
          </div>
        </div>

        <div className="border border-[#27272A] bg-[#121212]" data-testid="vacation-table">
          <div className="grid grid-cols-7 label-mute px-4 py-3 border-b border-[#27272A]">
            <div>Officer</div><div>From</div><div>To</div><div>Days</div><div>Reason</div><div>Status</div><div></div>
          </div>
          {loading && <div className="px-4 py-6 text-center text-zinc-500 text-xs font-mono">Loading…</div>}
          {!loading && visibleRows.length === 0 && (
            <div className="px-4 py-16 text-center text-zinc-500 scanlines">
              {rows.length === 0 ? "No requests yet." : "No requests in this window."}
            </div>
          )}
          {visibleRows.map((r) => {
            const days = Math.round((new Date(r.end_date) - new Date(r.start_date)) / 86400000) + 1;
            return (
              <div key={r.id} data-testid={`vacation-row-${r.id}`} className="grid grid-cols-7 items-center px-4 py-3 border-b border-[#27272A] text-xs hover:bg-[#1a1a1a]">
                <div className="font-medium">{r.guard_name}</div>
                <div className="font-mono">{r.start_date}</div>
                <div className="font-mono">{r.end_date}</div>
                <div className="font-mono">{days}</div>
                <div className="text-zinc-400 truncate" title={r.reason || ""}>{r.reason || "—"}</div>
                <div>
                  <PillBadge tone={toneFor(r.status)}>{r.status}</PillBadge>
                  {r.admin_note && <div className="text-[10px] text-zinc-500 mt-1 truncate" title={r.admin_note}>note: {r.admin_note}</div>}
                </div>
                <div className="text-right space-x-1">
                  {canDecide && r.status === "pending" && (
                    <>
                      <GhostButton data-testid={`vac-approve-${r.id}`} onClick={() => setDecidingId(r.id)} title="Approve (choose side effects)">
                        <Check className="size-3.5 text-[#34C759]" />
                      </GhostButton>
                      <GhostButton data-testid={`vac-reject-${r.id}`} onClick={() => decide(r.id, "rejected")} title="Reject">
                        <X className="size-3.5 text-[#FF3B30]" />
                      </GhostButton>
                    </>
                  )}
                  {(r.status === "pending" || isOp) && (
                    <GhostButton data-testid={`vac-delete-${r.id}`} onClick={() => remove(r.id)} title="Cancel/Delete">
                      <X className="size-3.5" />
                    </GhostButton>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {open && (
        <Modal title="New vacation request" onClose={() => setOpen(false)} testId="vacation-modal">
          <form onSubmit={submit} className="space-y-4">
            {isOp && (
              <FormField label="Officer *">
                <SingleSelectDropdown
                  testId="vac-guard"
                  label="Search & pick a "
                  options={guards.map((g) => ({ value: g.id, label: g.name || "Unknown", sub: g.badge_no ? `#${g.badge_no}` : "" }))}
                  value={form.guard_id}
                  onChange={(v) => setForm({ ...form, guard_id: v })}
                  searchable
                  triggerClassName="w-full"
                />
              </FormField>
            )}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="From">
                <input data-testid="vac-from" type="date" className={inputClass} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required />
              </FormField>
              <FormField label="To">
                <input data-testid="vac-to" type="date" className={inputClass} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required min={form.start_date || undefined} />
              </FormField>
            </div>
            <FormField label="Reason (optional)">
              <textarea className={inputClass} rows="3" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </FormField>
            <div className="flex justify-end">
              <ActionButton type="submit" data-testid="vac-save">Submit</ActionButton>
            </div>
          </form>
        </Modal>
      )}

      {decidingId && (
        <ApprovalOptionsModal
          onClose={() => setDecidingId(null)}
          onApprove={(opts) => decide(decidingId, "approved", opts)}
        />
      )}
    </div>
  );
}

/** Small modal shown when an operator approves a vacation. Lets them opt out
 * of the two automatic side-effects (add availability rows + unassign from
 * shifts) — useful when they want to override for a specific case. */
function ApprovalOptionsModal({ onClose, onApprove }) {
  const [addAvail, setAddAvail] = useState(true);
  const [unassign, setUnassign] = useState(true);
  return (
    <Modal title="Approve vacation request" onClose={onClose} testId="vacation-approve-modal">
      <div className="space-y-4 text-sm">
        <p className="text-zinc-400">
          Approving this request will also take these actions unless you opt out:
        </p>
        <label className="flex items-start gap-2 border border-[#27272A] p-3 cursor-pointer hover:border-zinc-500">
          <input type="checkbox" data-testid="vac-approve-add-availability"
            className="mt-0.5 accent-[#34C759]"
            checked={addAvail} onChange={(e) => setAddAvail(e.target.checked)} />
          <div>
            <div className="font-medium text-zinc-100">Add availability rows</div>
            <div className="text-xs text-zinc-500">Creates a &ldquo;vacation&rdquo; availability entry for every day in the window, skipping dates already recorded.</div>
          </div>
        </label>
        <label className="flex items-start gap-2 border border-[#27272A] p-3 cursor-pointer hover:border-zinc-500">
          <input type="checkbox" data-testid="vac-approve-unassign-shifts"
            className="mt-0.5 accent-[#FF9F0A]"
            checked={unassign} onChange={(e) => setUnassign(e.target.checked)} />
          <div>
            <div className="font-medium text-zinc-100">Remove  from shifts</div>
            <div className="text-xs text-zinc-500">Unassigns the  from any FUTURE shifts inside the window (release-tracked in the audit trail).</div>
          </div>
        </label>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#27272A]">
          <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
          <ActionButton
            data-testid="vac-approve-confirm"
            onClick={() => onApprove({ add_availability: addAvail, unassign_from_shifts: unassign })}
          >
            <Check className="size-4" /> Approve
          </ActionButton>
        </div>
      </div>
    </Modal>
  );
}
